/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  alpha,
  createTheme,
  darken,
  lighten,
  type PaletteOptions,
  SvgIcon,
  type SvgIconProps,
  type SxProps,
  type TextFieldProps,
  type Theme,
  useTheme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import type { RecentConfigRecord } from '@poweralpha/common';
import { get } from 'lodash';
import { createElement } from 'react';

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    tiny: true;
    xs: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    tiny: true;
    xs: true;
  }
}

export type ThemeColors = Record<'dark' | 'light', string>;

const checkboxViewBox = '0 0 20 20';

export const CheckboxUncheckedIcon = (props: SvgIconProps) =>
  createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
  );

export const CheckboxCheckedIcon = (props: SvgIconProps) => {
  const theme = useTheme();
  const markColor =
    theme.palette.mode === 'dark' ? brightNightColors.primaryContrastDark : '#FFFFFF';

  return createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'currentColor',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
    createElement('path', {
      d: 'M6.1 10.2L8.65 12.75L13.9 7.5',
      fill: 'none',
      stroke: markColor,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth: 2.1,
    }),
  );
};

export const CheckboxIndeterminateIcon = (props: SvgIconProps) => {
  const theme = useTheme();
  const markColor =
    theme.palette.mode === 'dark' ? brightNightColors.primaryContrastDark : '#FFFFFF';

  return createElement(
    SvgIcon,
    { ...props, viewBox: checkboxViewBox },
    createElement('rect', {
      x: 2.25,
      y: 2.25,
      width: 15.5,
      height: 15.5,
      rx: 4.5,
      fill: 'currentColor',
      stroke: 'currentColor',
      strokeWidth: 1.75,
    }),
    createElement('rect', {
      x: 5.5,
      y: 9,
      width: 9,
      height: 2,
      rx: 1,
      fill: markColor,
    }),
  );
};

const brightNightColors = {
  primaryLightMain: '#002C41',
  primaryLightDark: '#001F2E',
  primaryDarkMain: '#E1E1E1',
  primaryDarkLight: '#77B6C9',
  primaryDarkDark: '#B8B8B8',
  primaryContrastDark: '#1d1d1d',
  secondaryLightMain: '#E5B42F',
  teal: '#4A9BB5',
  tealLight: '#82C1D4',
  blue: '#0E63BE',
  blueLight: '#4E8FD7',
  gold: '#F7C738',
  goldLight: '#FFD24D',
  goldDark: '#D59D00',
  lightBackground: '#fafafa',
  lightPaper: '#fff',
  darkBackground: '#17191A',
  darkPaper: '#1F2224',
  skyMist: '#C8DCEA',
  ink: '#173145',
  slate: '#6C8699',
  moonlight: '#F2F8FC',
  steel: '#A9C0D0',
  scrollThumbDark: '#6B6B6B',
  success: '#6E9830',
  successLight: '#A4C55C',
  successDark: '#4D6C1F',
  warning: '#E39B23',
  warningLight: '#F0C260',
  warningDark: '#B87700',
  error: '#f04559',
  // error: '#f05567',
  // errorDarkModeMain: '#f04559',
  errorLight: '#FFD0C6',
  errorDark: '#C75137',
} as const;

export const lightTheme: PaletteOptions = {
  primary: {
    main: brightNightColors.primaryLightMain,
    light: brightNightColors.teal,
    dark: brightNightColors.primaryLightDark,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: brightNightColors.secondaryLightMain,
    light: brightNightColors.goldLight,
    dark: brightNightColors.goldDark,
    contrastText: brightNightColors.primaryLightMain,
  },
  info: {
    main: brightNightColors.teal,
    light: brightNightColors.tealLight,
    dark: brightNightColors.blue,
    contrastText: brightNightColors.primaryLightMain,
  },
  success: {
    main: brightNightColors.success,
    light: brightNightColors.successLight,
    dark: brightNightColors.successDark,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: brightNightColors.warning,
    light: brightNightColors.warningLight,
    dark: brightNightColors.warningDark,
    contrastText: brightNightColors.primaryLightMain,
  },
  error: {
    main: brightNightColors.error,
    light: brightNightColors.errorLight,
    dark: brightNightColors.errorDark,
    contrastText: '#FFFFFF',
  },
  background: {
    default: brightNightColors.lightBackground,
    paper: brightNightColors.lightPaper,
  },
  text: {
    primary: brightNightColors.ink,
    secondary: brightNightColors.slate,
  },
};

export const darkTheme: PaletteOptions = {
  primary: {
    main: brightNightColors.primaryDarkMain,
    light: brightNightColors.primaryDarkLight,
    dark: brightNightColors.primaryDarkDark,
    contrastText: brightNightColors.primaryContrastDark,
  },
  secondary: {
    main: brightNightColors.gold,
    light: brightNightColors.goldLight,
    dark: brightNightColors.goldDark,
    contrastText: brightNightColors.primaryLightMain,
  },
  info: {
    main: brightNightColors.blueLight,
    light: brightNightColors.skyMist,
    dark: brightNightColors.blue,
    contrastText: brightNightColors.primaryLightMain,
  },
  success: {
    main: brightNightColors.successLight,
    light: '#C6DD91',
    dark: brightNightColors.success,
    contrastText: brightNightColors.primaryLightMain,
  },
  warning: {
    main: brightNightColors.warningLight,
    light: '#F6D88D',
    dark: brightNightColors.warning,
    contrastText: brightNightColors.primaryLightMain,
  },
  error: {
    main: brightNightColors.error,
    light: brightNightColors.errorLight,
    dark: brightNightColors.errorDark,
    contrastText: brightNightColors.primaryLightMain,
  },
  background: {
    default: brightNightColors.darkBackground,
    paper: brightNightColors.darkPaper,
  },
  text: {
    primary: brightNightColors.primaryDarkMain,
    secondary: brightNightColors.steel,
  },
};

export const getSuperAdminBadgeColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.info.main, 0.06)
    : darken(theme.palette.info.dark, 0.18);

export const getSuperAdminBadgeBackgroundColor = (theme: Theme) =>
  alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.2 : 0.12);

type CommonColors = 'secondary' | 'primary' | 'info' | 'warning' | 'error';

export const highlightBackground = (
  theme: Theme, // FIXME: refactor to Theme['palette']
  opts?: {
    color?: string | CommonColors | Record<'dark' | 'light', string>;
    coefficient?: number | Record<'dark' | 'light', number>;
    reverse?: boolean;
  },
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: fine
) => {
  let color: string;
  if (opts?.color) {
    if (typeof opts.color === 'object' && 'dark' in opts.color && 'light' in opts.color) {
      color = opts.color[theme.palette.mode];
    } else if (opts.color in theme.palette) {
      color = theme.palette[opts.color as CommonColors].main;
    } else if (opts.color.includes('.')) {
      color = get(theme.palette, opts.color);
    } else {
      color = opts.color;
    }
  } else {
    color = theme.palette.background.paper;
  }

  const coefficient = opts?.coefficient ?? 0.1;
  const coeff = typeof coefficient === 'object' ? coefficient[theme.palette.mode] : coefficient;
  if (opts?.reverse) {
    return theme.palette.mode === 'dark' ? darken(color, coeff) : lighten(color, coeff);
  }
  return theme.palette.mode === 'dark' ? lighten(color, coeff) : darken(color, coeff);
};

export const blue = {
  100: '#DAECFF',
  200: '#b6daff',
  400: '#3399FF',
  500: '#007FFF',
  600: '#0072E5',
  900: '#003A75',
};

export const grey = {
  50: '#f6f8fa',
  100: '#eaeef2',
  200: '#d0d7de',
  300: '#afb8c1',
  400: '#8c959f',
  500: '#6e7781',
  600: '#57606a',
  700: '#424a53',
  800: '#32383f',
  900: '#24292f',
};

export const errorPalette: Record<'light' | 'dark', string> = {
  dark: brightNightColors.errorLight,
  light: brightNightColors.errorDark,
};

const scrollBarColors = {
  light: {
    thumb: brightNightColors.skyMist,
    activeThumb: brightNightColors.slate,
  },
  dark: {
    thumb: brightNightColors.scrollThumbDark,
    activeThumb: brightNightColors.steel,
  },
};

const scrollBarStyleOverrides = (mode: 'dark' | 'light') => {
  const { thumb, activeThumb } = scrollBarColors[mode];
  const track = 'transparent';
  return {
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      backgroundColor: `${thumb}`,
      minHeight: 24,
      border: `3px solid ${track}`,
      backgroundClip: 'content-box',
    },
    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
      backgroundColor: `${activeThumb}`,
    },
    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },
  };
};

export const textFieldBorderRadius = 10;
export const buttonBorderRadius = 10;
export const toggleButtonBorderRadius = 10;
export const alertBorderRadius = 12;
// Matches an `sx` borderRadius of 4 (4 x the default 4px shape radius).
export const popoverBorderRadius = 16;

export const getInputSurfaceColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.background.paper, 0.04)
    : darken(theme.palette.background.paper, 0.02);

export const getCompactTextFieldClassName = (size: TextFieldProps['size']) =>
  size === 'tiny' || size === 'xs' ? `pa-textfield-size-${size}` : undefined;

const tinyTextFieldSx = {
  '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    marginTop: '1px',
    height: 25,
    pr: 0,
    fontSize: 14,
    borderRadius: 8,
  },
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    pl: 1,
  },
  '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: 14,
  },
  '& .MuiPickersInputBase-sectionContent': {
    fontSize: 14,
    lineHeight: 1,
  },
  '& .MuiPickersInputBase-root .MuiInputAdornment-positionEnd, & .MuiPickersOutlinedInput-root .MuiInputAdornment-positionEnd':
    {
      marginLeft: 2,
      marginRight: -4,
    },
  '& .MuiPickersInputBase-root [data-mui-picker-open-button="true"], & .MuiPickersOutlinedInput-root [data-mui-picker-open-button="true"]':
    {
      padding: 4,
    },
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    height: 'auto',
    minHeight: 25,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    fontSize: 16,
  },
  '& .MuiInputLabel-root': {
    fontSize: 16,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend, & .MuiPickersOutlinedInput-notchedOutline legend': {
    fontSize: 12,
  },
};

const xsTextFieldSx = {
  '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    height: 30,
    pr: 0,
  },
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    pl: 1,
  },
  '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  '& .MuiPickersInputBase-sectionContent': {
    lineHeight: 1,
  },
  '& .MuiPickersInputBase-root .MuiInputAdornment-positionEnd, & .MuiPickersOutlinedInput-root .MuiInputAdornment-positionEnd':
    {
      marginLeft: 2,
      marginRight: -4,
    },
  '& .MuiPickersInputBase-root [data-mui-picker-open-button="true"], & .MuiPickersOutlinedInput-root [data-mui-picker-open-button="true"]':
    {
      padding: 4,
    },
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    height: 'auto',
    minHeight: 30,
  },
  '& .MuiInputLabel-root': {
    fontSize: 15,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend, & .MuiPickersOutlinedInput-notchedOutline legend': {
    fontSize: 11.3,
  },
};

export const getTextFieldSizeSx = (size: TextFieldProps['size']) => {
  if (size === 'tiny') {
    return tinyTextFieldSx;
  }
  if (size === 'xs') {
    return xsTextFieldSx;
  }
  return undefined;
};

export const getTheme = (themeMode: 'dark' | 'light') =>
  createTheme({
    palette: {
      mode: themeMode,
      ...(themeMode === 'dark' ? darkTheme : lightTheme),
    },
    typography: {
      h1: { fontSize: '2.25rem' },
      h2: { fontSize: '2rem' },
      h3: { fontSize: '1.75rem' },
      h4: { fontSize: '1.5rem' },
      h5: { fontSize: '1.25rem' },
      h6: { fontSize: '1.1rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: scrollBarStyleOverrides(themeMode),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const severity = ownerState.severity ?? 'success';
            const palette = theme.palette[severity];
            const backgroundAlpha = theme.palette.mode === 'dark' ? 0.24 : 0.16;
            const borderAlpha = theme.palette.mode === 'dark' ? 0.55 : 0.35;

            return {
              borderRadius: alertBorderRadius,
              padding: '0px 16px 0px',
              ...(ownerState.variant === 'standard' && {
                backgroundColor: alpha(palette.main, backgroundAlpha),
                border: `1px solid ${alpha(palette.main, borderAlpha)}`,
                color: theme.palette.text.primary,
                '& .MuiAlert-icon': {
                  color:
                    theme.palette.mode === 'dark' && severity !== 'warning'
                      ? palette.light
                      : palette.main,
                },
              }),
              ...(ownerState.variant === 'outlined' && {
                borderColor: alpha(palette.main, 0.7),
              }),
            };
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: () => ({
            borderRadius: buttonBorderRadius,
            '& .MuiButton-startIcon': {
              marginRight: 4,
            },
            '& .MuiButton-endIcon': {
              marginLeft: 4,
            },
            variants: [
              {
                props: {
                  size: 'tiny',
                  variant: 'text',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'text',
                },
                style: {
                  height: 30,
                },
              },
              {
                props: {
                  size: 'tiny',
                  variant: 'outlined',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'outlined',
                },
                style: {
                  height: 30,
                },
              },
              {
                props: {
                  size: 'tiny',
                  variant: 'contained',
                },
                style: {
                  height: 25,
                },
              },
              {
                props: {
                  size: 'xs',
                  variant: 'contained',
                },
                style: {
                  height: 30,
                },
              },
            ],
          }),
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: buttonBorderRadius,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => {
            const isDark = theme.palette.mode === 'dark';

            return {
              backgroundColor: isDark ? '#2b2b2b' : theme.palette.background.paper,
              backgroundImage: 'none',
              borderRadius: 16,
              color: theme.palette.text.primary,
              border: `1px solid ${
                isDark
                  ? alpha(theme.palette.common.white, 0.1)
                  : alpha(theme.palette.common.black, 0.12)
              }`,
              boxShadow: isDark
                ? '0 24px 80px rgba(0, 0, 0, 0.55)'
                : '0 24px 70px rgba(23, 49, 69, 0.18)',
              overflow: 'hidden',
              '& .MuiLink-root': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              },
              '& .MuiButton-root': {
                borderRadius: 8,
                textTransform: 'none',
              },
              ...(isDark
                ? {
                    '& .MuiButton-contained:not(.Mui-disabled):not(.MuiButton-colorError):not(.MuiButton-colorSuccess):not(.MuiButton-colorWarning):not(.MuiButton-colorInfo)':
                      {
                        backgroundColor: alpha(theme.palette.common.white, 0.78),
                        color: '#1f1f1f',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.common.white, 0.9),
                          boxShadow: 'none',
                        },
                      },
                    '& .MuiButton-contained.Mui-disabled': {
                      backgroundColor: alpha(theme.palette.common.white, 0.18),
                      color: alpha(theme.palette.common.white, 0.52),
                    },
                  }
                : {
                    '& .MuiButton-contained.Mui-disabled': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.12),
                      color: alpha(theme.palette.text.primary, 0.38),
                    },
                  }),
            };
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            padding: '8px 16px 0',
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            padding: '8px 16px 8px',
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '8px 16px 12px',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(getTextFieldSizeSx(ownerState.size) ?? {}),
            '&.pa-textfield-size-tiny': tinyTextFieldSx,
            '&.pa-textfield-size-xs': xsTextFieldSx,
          }),
        },
      },
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            '&.pa-textfield-size-tiny': tinyTextFieldSx,
            '&.pa-textfield-size-xs': xsTextFieldSx,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: buttonBorderRadius,
            '& .MuiTouchRipple-root': {
              inset: 4,
              borderRadius: 8,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: textFieldBorderRadius,
            backgroundColor: getInputSurfaceColor(theme),
          }),
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: toggleButtonBorderRadius,
            overflow: 'hidden',
          },
          grouped: {
            borderRadius: 0,
          },
          firstButton: {
            borderRadius: 0,
            borderTopLeftRadius: toggleButtonBorderRadius,
            borderBottomLeftRadius: toggleButtonBorderRadius,
          },
          middleButton: {
            borderRadius: 0,
          },
          lastButton: {
            borderRadius: 0,
            borderTopRightRadius: toggleButtonBorderRadius,
            borderBottomRightRadius: toggleButtonBorderRadius,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: toggleButtonBorderRadius,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          icon: createElement(CheckboxUncheckedIcon),
          checkedIcon: createElement(CheckboxCheckedIcon),
          indeterminateIcon: createElement(CheckboxIndeterminateIcon),
        },
        styleOverrides: {
          root: ({ theme }) => ({
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiTouchRipple-root': {
              inset: 9,
              borderRadius: 4,
            },
            '& .MuiSvgIcon-root': {
              borderRadius: 17,
            },
            '&:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(
                theme.palette.action.active,
                theme.palette.action.hoverOpacity,
              ),
            },
            '&.MuiCheckbox-colorPrimary:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
            },
            '&.MuiCheckbox-colorSecondary:hover .MuiSvgIcon-root': {
              backgroundColor: alpha(
                theme.palette.secondary.main,
                theme.palette.action.hoverOpacity,
              ),
            },
            '@media (hover: none)': {
              '&:hover .MuiSvgIcon-root': {
                backgroundColor: 'transparent',
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }),
          action: {
            alignSelf: 'center',
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            '&:last-child': {
              paddingBottom: theme.spacing(1.5),
            },
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: popoverBorderRadius,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            mx: 0,
            '& .MuiChip-label': {
              paddingLeft: 6,
              paddingRight: 6,
            },
            '&.MuiChip-sizeSmall .MuiChip-label': {
              paddingLeft: 4,
              paddingRight: 4,
            },
          },
        },
      },
    },
  });

export enum PlotColors {
  LIGHT_BLUE = 'light_blue',
  GOLD = 'gold',
  BLUE = 'blue',
}

export const plotColorsDark: Record<PlotColors, string> = {
  blue: '#004475',
  gold: '#FDBF0B',
  light_blue: '#3E96B5',
};

export const plotColorsLight: Record<PlotColors, string> = {
  blue: '#002641',
  gold: '#FDBF0B',
  light_blue: '#3E96B5',
};

export const salesforceColor = '#21A0E0';

export const getPlotColor = (theme: Theme, color: PlotColors) =>
  theme.palette.mode === 'dark' ? plotColorsDark[color] : plotColorsLight[color];

export type RgbString =
  | `rgb(${number},${number},${number})`
  | `rgb(${number}, ${number}, ${number})`;

export const getRgbaColor = (color: RgbString, opacity: number) =>
  color.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${opacity})`);

export const hexToRgba = (hex: `#${string}`, opacity: number) => {
  let r = 0;
  let g = 0;
  let b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = Number.parseInt(hex[1] + hex[1], 16);
    g = Number.parseInt(hex[2] + hex[2], 16);
    b = Number.parseInt(hex[3] + hex[3], 16);
  }
  // 6 digits
  else if (hex.length === 7) {
    r = Number.parseInt(hex[1] + hex[2], 16);
    g = Number.parseInt(hex[3] + hex[4], 16);
    b = Number.parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const overrideSx = (
  customSx: SystemStyleObject<Theme>,
  theme: Theme,
  userDefinedSx?: SxProps<Theme>,
): SystemStyleObject<Theme> => {
  if (!userDefinedSx) return customSx;
  if (Array.isArray(userDefinedSx)) {
    throw new Error('Array type is not supported for sx prop in overrideSx function');
  }

  // @ts-expect-error this works for our purposes
  return {
    ...customSx,
    ...(typeof userDefinedSx === 'function' ? userDefinedSx(theme) : userDefinedSx),
  };
};

export const serviceColorMap: Record<RecentConfigRecord['service'], ThemeColors> = {
  PVCosting: { dark: '#4caf50', light: '#4caf50' },
  ESS: { dark: '#2196f3', light: '#2196f3' },
  PVSim: { dark: '#ebcb00', light: '#9a8500' },
  PlantSim: { dark: '#ae58e7', light: '#841dc9' },
};

export const bodColor = '#ffc31d';
